<template>
  <div class="basket">
    <h1 class="h4 mb-4">
      <template v-if="!module.switchOrderOfferTitle">{{ $t(`${tPath}.title`) }}</template>
      <template v-else>
        <template v-if="isQuote">{{ $t(`${tPath}.quotetitle`) }}</template>
        <template v-else>{{ $t(`${tPath}.title`) }}</template>
      </template>
      <span v-if="$te(`${tPath}.subtitle`)" class="ml-4 h5 font-weight-normal text-dark">{{ $t(`${tPath}.subtitle`, { count: basket.itemCount }) }}</span>
    </h1>

    <b-alert v-if="quoteValidUntil" variant="info" :show="true">
      <strong>{{ $t(`${tPath}.quote.validuntilmessage`, { quoteValidUntil: $d(new Date(quoteValidUntil)) }) }}</strong>
    </b-alert>

    <slot name="aftertitle" v-bind="{ isLoading: is.initial }" />

    <loader v-if="is.initial" variant="primary"/>

    <div v-else-if="basket.itemCount <= 0" class="basket-emptymessage">
      <c-intro-banner variant="beige">
        <b-row align-v="center">
          <slot name="emptymessage"/>
        </b-row>
      </c-intro-banner>
    </div>

    <b-row v-else>
      <b-col :[$root.md.key]="module.showSummary ? 8 : 12" tag="article">
        <slot name="before-basket" v-bind="{ basket, configuration: basket.configuration }" />

        <div :class="['basket-items', { 'no-quantity': !module.showItemQuantity, 'no-totalprice': !module.showItemTotalprice }]">
          <div class="items-header">
            <div class="area-description">{{ $t(`${tPath}.items.header.description`) }}</div>
            <div v-if="module.showItemQuantity" class="area-quantity">{{ $t(`${tPath}.items.header.quantity`) }}</div>
            <div class="area-price">{{ $t(`${tPath}.items.header.price`) }}</div>
            <div v-if="showItemDiscount" class="area-discount">{{ $t(`${tPath}.items.header.discount`) }}</div>
            <div v-if="module.showItemTotalprice" class="area-totalprice">{{ $t(`${tPath}.items.header.totalprice`) }}</div>
          </div>

          <div class="items-main">
            <basket-item
              v-for="item in basket.items"
              :key="item.basketItemId"
              :moduleName="moduleName"
              :item="item"
              :issuedGiftCard="issuedGiftCards.find(g => g.basketItemId === item.basketItemId)"
              :translationsPath="tPath"
              :showItemDiscount="showItemDiscount"
              :isStoreClerk="isStoreClerk"
              :showStoreClerkOptions="isStoreClerk"
              @basketitem:update="$item => updateItem($item)"
              @basketitem:delete="$item => deleteItem($item)"
            >
              <template #basketitemservicebuttons="slotProps">
                <slot name="basketitemservicebuttons" v-bind="slotProps"/>
              </template>
            </basket-item>
          </div>

          <div v-if="module.showActionButtons" class="items-footer">
            <div class="area-basketactions-start">
              <div class="footer-actions">
                <a href="#" @click="deleteBasket" class="delete"><lazy-icon icon="trash"/> {{ $t(`${tPath}.items.footer.action.deletebasket`) }}</a>
              </div>
            </div>

            <div class="area-basketactions-end">
              <div class="footer-actions">
                <a href="#" @click="printBasket"><lazy-icon icon="printer"/> {{ $t(`${tPath}.items.footer.action.print`) }}</a>
              </div>
            </div>
          </div>
        </div>

        <slot name="after-basket" v-bind="{ basket, configuration: basket.configuration }" />
      </b-col>

      <b-col v-if="module.showSummary" :[$root.md.key]="4" tag="aside" :class="{ 'mt-3': !$root.isDesktop }">
        <slot name="before-summary" v-bind="{ tPath, basket }"/>

        <checkout-summary :moduleName="moduleName" :preloadedBasket="preloadedBasket">
          <template #title>
            {{ $t(`${tPath}.summary.title`) }}
          </template>

          <template #content>
            <slot name="summaryfooter" v-bind="{ submit }"/>
          </template>
        </checkout-summary>

        <slot name="after-summary" v-bind="{ tPath }"/>
      </b-col>
    </b-row>

    <div v-if="module.showAlsoBought">
      <v-runtime-template :template="alsoBoughtProducts" />
    </div>
  </div>
</template>

<script>
import VRuntimeTemplate from 'v-runtime-template'
import { COMPONENT_BASKET_MODULES } from '@/constants'

import Loader from '@/components/private/Loader'
import BasketItem from '@/components/private/BasketItem'
import CheckoutSummary from '@/components/private/CheckoutSummary'

const DEFAULT_MODULENAME = Object.keys(COMPONENT_BASKET_MODULES).find(mKey => COMPONENT_BASKET_MODULES[mKey].isDefault)

export default {
  name: 'Basket',
  components: {
    Loader,
    BasketItem,
    CheckoutSummary,
    VRuntimeTemplate
  },
  props: {
    moduleName: {
      type: String,
      default: DEFAULT_MODULENAME
    },
    preloadedBasket: {
      type: Object,
      default: null
    },
    issuedGiftCards: {
      type: Array,
      default: () => []
    },
    showItemDiscount: {
      type: Boolean,
      default: false
    },
    isStoreClerk: {
      type: Boolean,
      default: false
    },
    isQuote: {
      type: Boolean,
      default: false
    },
    quoteValidUntil: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      alsoBoughtProducts: null
    }
  },
  computed: {
    module () {
      return Object.assign({}, COMPONENT_BASKET_MODULES.base, COMPONENT_BASKET_MODULES[this.moduleName] ?? {})
    },
    is () {
      return this.module.isPreloaded ? {} : this.$store.getters[`${this.module.name}/is`]
    },
    basket () {
      return this.module.isPreloaded ? this.preloadedBasket : this.$store.getters[`${this.module.name}/${this.module.getters.basket}`]
    },
    basketProductIds () {
      return (this.basket.items || []).map(i => i.product.id)
    }
  },
  methods: {
    initBasket (isInitialCall = false) {
      if (!this.module.isPreloaded) {
        this.$store.dispatch(`${this.module.name}/${this.module.actions.get}`)
          .then(() => {
            if (isInitialCall || (!isInitialCall && !this.module.gtm.initOnly)) this.gtm()
          })
      } else {
        if (isInitialCall || (!isInitialCall && !this.module.gtm.initOnly)) this.gtm()
      }
    },
    printBasket () {
      window.print()
    },
    deleteBasket () {
      this.$store.dispatch(`${this.module.name}/${this.module.actions.delete}`)
    },
    updateItem (item) {
      this.$store.dispatch(`${this.module.name}/${this.module.actions.updateItem}`, { item, omitLastAction: true })
    },
    deleteItem (item) {
      this.$store.dispatch(`${this.module.name}/${this.module.actions.deleteItem}`, { item, omitLastAction: true, gtmList: this.module.gtm.list })
    },
    updateAlsoBoughtProducts() {
      if (!this.module.showAlsoBought) {
        return
      }

      const basketProductIds = this.basketProductIds
      if (basketProductIds.length <= 0) {
        this.alsoBoughtProducts = null
        return
      }

      const alsoBoughtProductUrl = `${window.location.origin}/checkout/alsoboughtproducts?productIds=${basketProductIds.join(',')}`
      this.$http({
        method: 'get',
        url: alsoBoughtProductUrl
      })
        .then((response) => {
          const contentMatch = response.data.match(/(?:<div id="AlsoBoughtCarousel".*?>)([\s\S]*)(?:<\/div>)/)
          if (contentMatch === null || contentMatch.length < 1) {
            this.alsoBoughtProducts = null
            return
          }
          const contentDom = new DOMParser().parseFromString(contentMatch[1] ?? '', 'text/html').body.innerHTML
          this.alsoBoughtProducts = contentDom
        })
        .catch((error) => {
          console.warn(error)
          this.alsoBoughtProducts = null
        })
    },
    beforePrint () {
      document.documentElement.classList.add('basket-print-mode')
    },
    afterPrint () {
      document.documentElement.classList.remove('basket-print-mode')
    },
    submit (e) {},
    gtm () {
      this.$nextTick(() => {
        if (this.module.gtm.step !== undefined) {
          this.$store.dispatch('gtm/checkout', {
            step: this.module.gtm.step,
            list: this.module.gtm.list,
            products: this.basket.items.map(i => Object.assign({}, i.product, { quantity: i.quantity }))
          })
        } else if (this.module.gtm.list !== undefined) {
          this.$store.dispatch('gtm/productImpressions', {
            list: this.module.gtm.list,
            products: this.basket.items.map(i => Object.assign({}, i.product))
          })
        }
      })
    }
  },
  created () {
    this.initBasket(true)
    this.updateAlsoBoughtProducts()

    window.addEventListener('beforeprint', this.beforePrint)
    window.addEventListener('afterprint', this.afterPrint)
  },
  beforeDestroy () {
    window.removeEventListener('beforeprint', this.beforePrint)
    window.removeEventListener('afterprint', this.afterPrint)
  },
  watch: {
    'basket.id' (newValue, oldValue) {
      if (newValue !== oldValue) this.initBasket()
    },
    'basket.itemCount' (newValue, oldValue) {
      if (newValue !== oldValue && this.basket.setFromLocalStorage) {
        this.initBasket()
        this.$store.commit(`${this.module.name}/resetItemCountSetFromStorage`)
      }
    },
    basketProductIds (newValue, oldValue) {
      if (newValue.length <= 0) {
        this.alsoBoughtProducts = null
        return
      }

      if (newValue.length !== oldValue.length || newValue.some((id, index) => id !== oldValue[index])) {
        this.updateAlsoBoughtProducts()
      }
    }
  }
}
</script>

<style lang="scss">
// mobile

$basket-emptymessage-content-text-align: center !default;

$basket-items-grid-column-gap: $grid-gutter-width * 0.5 !default;
$basket-items-grid-row-gap: $spacer * 0.4 !default;
$basket-items-grid-columns: [start] auto 4fr 2fr 2fr 2fr 1.5rem [end] !default;
$basket-items-grid-rows: min-content auto min-content !default;
$basket-items-grid-area-gap-y: $spacer !default;

$basket-items-footer-actions-gap-y: $spacer * 0.5 !default;
$basket-items-footer-actions-gap-x: $spacer * 2 !default;

// tablet

$basket-tablet: $tablet-breakpoint !default;

$basket-tablet-emptymessage-content-text-align: inherit !default;

$basket-tablet-items-grid-columns: [start] auto 4fr auto auto auto auto [end] !default;

$basket-tablet-items-header-font-size: $font-size-sm !default;
$basket-tablet-items-header-color: $dark !default;
$basket-tablet-items-header-line-height: 1.1 !default;

// desktop

$basket-desktop: $desktop-breakpoint !default;

.basket {
  @include component('y');

  .basket-emptymessage {
    @include component('y');
    margin-top: 0!important;

    .emptymessage-image {}

    .emptymessage-content {
      text-align: $basket-emptymessage-content-text-align;
    }
  }

  .basket-items {
    display: grid;
    column-gap: $basket-items-grid-column-gap;
    row-gap: $basket-items-grid-row-gap;
    grid-template-columns: $basket-items-grid-columns;
    grid-template-rows: $basket-items-grid-rows;

    [class*="area-"] {
      grid-row: auto;
    }

    .area-image,
    .area-configuration,
    .area-quantity {
      margin-bottom: $basket-items-grid-area-gap-y;
    }

    .area-separator {
      grid-row-gap: 0;
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-image {
      grid-column: start / 6;
      justify-self: stretch;
    }

    .area-actions {
      grid-column: 6 / end;
      justify-self: flex-end;
    }

    .area-description {
      grid-column: start / end;
    }

    .area-configuration {
      grid-column: start / end;
    }

    .area-quantity {
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-price {
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-discount {
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-totalprice {
      grid-column: start / end;
      justify-self: stretch;
    }

    .area-services {
      grid-column: start / end;
      justify-self: flex-end;
    }

    .items-header {
      display: none;
    }

    .items-main {
      display: contents;
    }

    .items-footer {
      display: contents;

      .area-basketactions-start {
        grid-column: start / end;
      }

      .area-basketactions-end {
        margin-top: $basket-items-footer-actions-gap-y;
        grid-column: start / end;
      }

      .footer-actions {
        margin: ($basket-items-footer-actions-gap-y * -0.5) ($basket-items-footer-actions-gap-x * -0.5);

        a {
          display: block;
          padding: ($basket-items-footer-actions-gap-y * 0.5) ($basket-items-footer-actions-gap-x * 0.5);
        }
      }
    }
  }

  .basket-payment{
    list-style: none;
    display:flex;
    padding-left:0;
    flex-wrap:wrap;

    li{
    margin-right:5px;
    }

    img{
      margin-bottom:0px;
    }
  }

  @include media-breakpoint-up($basket-tablet) {
    .basket-emptymessage {
      .emptymessage-image {}

      .emptymessage-content {
        text-align: $basket-tablet-emptymessage-content-text-align;
      }
    }

    .basket-items {
      grid-template-columns: $basket-tablet-items-grid-columns;

      .area-image,
      .area-description,
      .area-configuration,
      .area-quantity {
        margin-bottom: 0;
      }

      .area-image {
        grid-column: start;
        grid-row: auto / span 2;
      }

      .area-description {
        grid-column: 2;
      }

      .area-quantity {
        grid-column: 3;
        justify-self: center;
      }

      .area-price {
        grid-column: 4;
        justify-self: flex-end;
      }

      .area-discount {
        grid-column: 5;
        justify-self: flex-end;
      }

      .area-totalprice {
        grid-column: 6;
        justify-self: flex-end;
      }

      .area-configuration {
        grid-column: 2 / span 4;
        align-self: center;
      }

      .area-actions {
        grid-column: 6;
        align-self: flex-end;
      }

      .area-services {
        grid-column: start / end;
      }

      .items-header {
        display: contents;
        font-size: $basket-tablet-items-header-font-size;
        color: $basket-tablet-items-header-color;
        line-height: $basket-tablet-items-header-line-height;

        .area-description {
          grid-column: start / 3;
        }
      }

      .items-footer {
        .area-basketactions-start {
          grid-column: start / 4;
        }

        .area-basketactions-end {
          grid-column: 4 / end;
          justify-self: flex-end;
          margin-top: 0;
        }

        .footer-actions {
          display: flex;
        }
      }

      &.no-quantity {
        .area-price {
          grid-column: 3 / span 2;
        }
      }

      &.no-totalprice {
        .area-price {
          grid-column: 4 / span 2;
        }
      }

      &.no-quantity.no-totalprice {
        .area-price {
          grid-column: 3 / span 3;
        }
      }
    }
  }
}

@media print {
  .basket-print-mode {
    #app {
      > header,
      > footer {
        display: none!important;
      }

      > main {
        aside {
          display: none!important;
        }
      }
    }
  }
}
</style>
